import _                     from 'underscore';
import { v4 as uuid }        from 'uuid';
import { reactLocalStorage } from 'reactjs-localstorage';

import { capitalize, stringToColor }         from '@src/util';
import { CONSTANTS, EMPTY_VALUE_DISPLAY }    from '@src/constants';
import { CorvalAccountLike, CorvalUserLike } from '@src/types';


type validateUserAttributeTypes = 'lastName' | 'firstName' | 'emailAddress';

export class UserService {

    static emptyUsernameString = EMPTY_VALUE_DISPLAY;
    static emptyEmailString = EMPTY_VALUE_DISPLAY;

    // Retrieves the schema for the auth cookie, optionally with a speecified attribute
    static getAuthTokenStorageSchema( attr: string = null ){

        //@ts-ignore
        let environment = __ENV__;

        return attr
               ? environment.session.cookies.auth[ attr ]
               : environment.session.cookies.auth;
    };

    static getAuthTokenStorageKeyName(): string{

        return UserService.getAuthTokenStorageSchema( 'namedKey' )
    }

    // Sets the auth cookie value
    static setAuthToken( authToken: string = null ){

        if( !( authToken ) ){
            throw new Error( '[UserService]: Cannot set authorization token storage without an auth token passed in.' );
        }

        let authKey = UserService.getAuthTokenStorageKeyName();

        reactLocalStorage.set( authKey, authToken );
    }

    // Gets the auth cookie value
    static getAuthToken(){

        return reactLocalStorage.get( UserService.getAuthTokenStorageKeyName() );
    }

    // Boolean if auth cookie exists
    static authTokenAvailable(){

        return ( reactLocalStorage.get( UserService.getAuthTokenStorageKeyName() ) !== undefined );
    }

    // Removes the auth cookie from system
    static invalidateAuthToken(){

        reactLocalStorage.remove( UserService.getAuthTokenStorageKeyName() );

        return new Date();
    }

    static _validUserObject( user = {}, validateOnAttributes: validateUserAttributeTypes[] = [] ){

        let attributes = [
            'lastName',
            'firstName',
            'emailAddress',
        ];

        let userAttributes = ( _.pick( user, attributes ) as validateUserAttributeTypes[] );

        if( !_.isEmpty( validateOnAttributes ) ){

            let validAttributes = _.compact( validateOnAttributes.map( attr => {

                if( !_.contains( attributes, attr ) ){
                    throw new Error( `User object cannot validate the '${ attr }' attribute in UserService._validUserObject` )
                }

                return (
                           _.isNull( userAttributes[ attr ] ) ||
                           _.isUndefined( userAttributes[ attr ] )
                       ) ? null : attr;

            } ) );

            return ( _.compact( validAttributes ).length === validateOnAttributes.length );
        }

        return ( _.compact( userAttributes ).length === attributes.length );
    }

    // Produces a user's formatted name
    static getFormattedUserName( user: CorvalUserLike = null, formatType = CONSTANTS.FORMATTING.USERNAMES.FULL_LASTNAME_FIRST ){

        let usernameFormatFuncs = {
            [ CONSTANTS.FORMATTING.USERNAMES.FULL ]:                UserService.getUserName,
            [ CONSTANTS.FORMATTING.USERNAMES.FULL_LASTNAME_FIRST ]: _.partial( UserService.getUserName, _, true ),
            [ CONSTANTS.FORMATTING.USERNAMES.SHORT ]:               UserService.getShortUserName,
            [ CONSTANTS.FORMATTING.USERNAMES.INITIALS ]:            UserService.getUserInitials,
        };

        return user ? usernameFormatFuncs[ formatType ]( user ) : null;
    }

    // Produces a user's name
    static getUserName( user: CorvalUserLike, lastNameFormat = false ){

        if( !UserService._validUserObject( user, [ 'firstName', 'lastName' ] ) ) return UserService.emptyUsernameString;

        let { firstName, lastName } = user;

        return ( lastNameFormat )
               ? capitalize( lastName + ', ' + firstName )
               : capitalize( firstName + ' ' + lastName );
    }

    // Produces a user's short name
    static getShortUserName( user: CorvalUserLike ){

        if( !UserService._validUserObject( user, [ 'firstName', 'lastName' ] ) ) return UserService.emptyUsernameString;

        let { firstName, lastName } = user;

        if( firstName.length ){

            //Shorten firstname to initial
            firstName = `${ firstName.substring( 0, 1 ) }.`;
        }

        return capitalize( `${ _.compact( [ firstName, lastName ] ).join( ' ' ) }` );
    }

    // Produces a user's initials
    static getUserInitials( user: CorvalUserLike ){

        if( !UserService._validUserObject( user, [ 'firstName', 'lastName' ] ) ) return UserService.emptyUsernameString;

        let { firstName, lastName } = user,
            initials                = [
                firstName.substring( 0, 1 ),
                lastName.substring( 0, 1 )
            ];

        return initials.join( '' ).toUpperCase();
    }

    // Produces a user's email
    static getUserEmail( user: CorvalUserLike & Omit<CorvalAccountLike, 'id'> ){

        if( !UserService._validUserObject( user, [ 'emailAddress' ] ) ) return UserService.emptyEmailString;

        return user.emailAddress;
    }

    // Produces a user's color
    static getUserColor( user: CorvalUserLike ){

        let username = ( !UserService._validUserObject( user, [ 'firstName', 'lastName' ] ) ) ? uuid() : UserService.getUserName( user );

        return stringToColor( username, 65, 50 );
    }
}
